import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { SignalrEvent } from '@wdx/shared/utils';
import { of } from 'rxjs';
import {
    catchError,
    filter,
    map,
    mergeMap,
    switchMap,
    withLatestFrom,
} from 'rxjs/operators';
import { GLOBAL_STATE_INDEX_ID } from '../../constants/state.constants';
import * as rootReducer from '../../state/_setup/reducers';
import * as partiesRoleSelector from '../../state/party-role/party-role.selectors';
import { newSystemSignalrEvent } from '../system-events/system-events.actions';
import * as partieyRoleActions from './party-role.actions';
import * as partyRoleActions from './party-role.actions';
import { PartyRoleService } from './party-role.service';

@Injectable()
export class PartyRoleEffects {
    private actions$ = inject(Actions);
    private store$ = inject(Store<rootReducer.State>);
    private partyRoleService = inject(PartyRoleService);

    getForId$ = createEffect(() =>
        this.actions$.pipe(
            ofType(partyRoleActions.getForId),
            mergeMap((action) =>
                this.partyRoleService.getForId(action.partyRoleId).pipe(
                    map((partyRole) =>
                        partyRoleActions.getForIdSuccess({
                            partyRoleId: action.partyRoleId,
                            partyRole,
                        }),
                    ),
                    catchError((error) =>
                        of(
                            partyRoleActions.getForIdFailure({
                                partyRoleId: action.partyRoleId,
                                error,
                            }),
                        ),
                    ),
                ),
            ),
        ),
    );
    getTasksForPartyRole$ = createEffect(() =>
        this.actions$.pipe(
            ofType(partyRoleActions.getAllTasksForPartyRole),
            withLatestFrom(
                this.store$.select(
                    partiesRoleSelector.getTasksForPartyRoleInfinityPaging,
                    {
                        partyRoleId: GLOBAL_STATE_INDEX_ID,
                    },
                ),
            ),
            switchMap(([action, paging]) =>
                this.partyRoleService
                    .getAllTasksForPartyRole(
                        {
                            pageNumber: (paging?.page || 0) + 1,
                        },
                        action.partyRoleId,
                    )
                    .pipe(
                        map((tasks) =>
                            partyRoleActions.getAllTasksForPartyRoleSuccess({
                                partyRoleId: action.partyRoleId,
                                tasks,
                            }),
                        ),
                        catchError((error) =>
                            of(
                                partyRoleActions.getAllTasksForPartyRoleFailure(
                                    {
                                        partyRoleId: action.partyRoleId,
                                        error,
                                    },
                                ),
                            ),
                        ),
                    ),
            ),
        ),
    );

    listenToPartyRoleUpdatedEvents$ = createEffect(() =>
        this.actions$.pipe(
            ofType(newSystemSignalrEvent),
            filter(
                ({ event }) =>
                    event.signalrEvent === SignalrEvent.PartyRoleUpdated,
            ),
            map(({ event }) =>
                partieyRoleActions.partyRoleUpdatedSuccess({
                    partyRoleId: event.data as string,
                }),
            ),
        ),
    );

    listenToPartyRoleCasesUpdatedEvents$ = createEffect(() =>
        this.actions$.pipe(
            ofType(newSystemSignalrEvent),
            filter(
                ({ event }) =>
                    event.signalrEvent === SignalrEvent.PartyRoleCasesUpdated,
            ),
            map(({ event }) =>
                partieyRoleActions.partyRoleCasesUpdatedSuccess({
                    partyRoleId: event.data as string,
                }),
            ),
        ),
    );
}
